import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  formatPostIndiFromRecord,
  AddToFamilyTreeButton,
} from '@ltvco/refresh-lib/fmt';
import { openReportInNewTab } from '@ltvco/refresh-lib/hooks';
import {
  DarkTooltip,
  useTheme,
  Stack,
  Box,
  Text,
  Button,
} from '@ltvco/refresh-lib/theme';
import { DeceasedTag, AgeUncertaintyHint } from '@ltvco/refresh-lib/v2';
import {
  Person,
  Spellcheck,
  Cake,
  LocationOn,
  People,
} from '@mui/icons-material';
import { useContext } from 'react';

import { constants } from 'appConstants';
import { useSearchPersonResultsContext } from 'routes/SearchPerson/context';

import { BaseCard } from '../BaseCard';
import {
  PersonIconWrapper,
  InfoSection,
  NameTypography,
  SecondaryTextTypography,
  SecondaryTextSubtitleTypography,
  CustomList,
  RelativesList,
} from '../common/components';
import { PersonCardData } from '../common/types';
import { formatName, getUniqueRelatives } from '../common/utils';

// Helper to clean location strings (remove leading commas and trim)
const cleanLocationString = (loc: string): string => {
  return loc.trim().replace(/^,\s*/, '');
};

// Helper to format items with +X more
const formatItemsWithMore = (
  items: string[] | undefined,
  maxItems: number = 2,
  cleanLocations: boolean = false
): string => {
  if (!items || items.length === 0) return '-';
  const cleanedItems = cleanLocations
    ? items.map(cleanLocationString).filter((item) => item)
    : items.filter((item) => item && item.trim());
  if (cleanedItems.length === 0) return '-';
  const displayed = cleanedItems.slice(0, maxItems).join(', ');
  const remaining = cleanedItems.length - maxItems;
  return remaining > 0 ? `${displayed} +${remaining}` : displayed;
};

export function MobilePersonCard({
  bvid,
  name,
  nameMatched,
  deceased,
  uncertainlyDeceased,
  birthInfo,
  locations,
  aliases,
  relatives,
  onSuccess,
  relativesNotSerialized,
  handleAddToFamilyTree,
  reportInTree,
  isCreatingFamilyTree,
  personData,
}: PersonCardData) {
  const theme = useTheme();
  const { trackEventGA4 } = useContext(AppConfig);
  const { handlePersonReport, isChatPage } = useSearchPersonResultsContext();
  const { uniqueRelatives, uniqueRelativesIds } = getUniqueRelatives(
    relatives,
    relativesNotSerialized
  );

  const openReport = (bvid: string) => {
    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'primary cta',
      interacted_from: 'Person Records',
    });
    openReportInNewTab({ bvid, searchType: 'person' }, constants.links.baseUrl);
  };

  const handleRelativeClick = (index: number) => {
    const relativeBvId = uniqueRelativesIds[index];
    if (!relativeBvId) return;

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'relative cta',
      interacted_from: 'Person Records',
    });

    if (isChatPage) {
      handlePersonReport?.(relativeBvId);
      return;
    }
    openReport(relativeBvId);
  };

  const handleOpenMainPersonReport = () => {
    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'primary cta',
      interacted_from: 'Person Records',
    });

    trackEventGA4({
      event_name: 'viewed_report',
      interacted_from: 'search_results',
      report_type: 'person',
    });
    onSuccess?.();
    if (isChatPage) {
      handlePersonReport?.(bvid);
      return;
    }
    openReport(bvid);
  };

  const onHandleAddToFamilyTree = () => {
    if (!personData) return;

    const postIndi = formatPostIndiFromRecord(personData);
    if (!postIndi) return;

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'fam tree cta',
      interacted_from: 'Person Records',
    });
    handleAddToFamilyTree(postIndi);
  };

  const formattedName = formatName(name || '');
  const safeName = name || '';

  // Format locations and relatives for collapsed view
  const formattedLocations = formatItemsWithMore(
    locations as string[],
    2,
    true
  );
  const formattedRelatives = formatItemsWithMore(uniqueRelatives, 2);

  return (
    <BaseCard
      name={safeName}
      isExpandable={true}
      onDetailsClick={handleOpenMainPersonReport}
      tabName="Person"
      renderIcon={() => (
        <PersonIconWrapper sx={{ width: '32px', height: '32px' }}>
          <Person color="primary" sx={{ fontSize: '20px' }} />
        </PersonIconWrapper>
      )}
      renderHeader={() => (
        <Stack direction={'column'} spacing={1}>
          <NameTypography variant="subtitle1" fontWeight="bold" mb={0}>
            {formattedName}
            <Stack flexDirection="row" alignItems="center">
              {uncertainlyDeceased && (
                <AgeUncertaintyHint sx={{ marginLeft: theme.spacing(0.2) }} />
              )}
              {deceased && (
                <DeceasedTag marginLeft={theme.spacing(1)} component="span">
                  Deceased
                </DeceasedTag>
              )}
              {nameMatched && (
                <DarkTooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={2000}
                  title="Name Matched"
                >
                  <Spellcheck
                    className="SpellCheck"
                    sx={{
                      height: '16px',
                      width: '16px',
                      margin: 'auto',
                      color: theme.palette.neighborhoodWatch?.main,
                      ml: theme.spacing(0.2),
                      mr: theme.spacing(0.2),
                    }}
                  />
                </DarkTooltip>
              )}
            </Stack>
          </NameTypography>
          <Stack direction={'column'} spacing={1}>
            {/* Birth info with icon */}
            {birthInfo && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                <Cake
                  sx={{ fontSize: '16px', color: theme.palette.text.secondary }}
                />
                <SecondaryTextTypography variant="body2" sx={{ margin: 0 }}>
                  {deceased ? `Born: ${birthInfo}` : birthInfo}
                </SecondaryTextTypography>
              </Stack>
            )}

            {/* Locations with icon */}
            {formattedLocations && formattedLocations !== '-' && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                <LocationOn
                  sx={{ fontSize: '16px', color: theme.palette.text.secondary }}
                />
                <SecondaryTextTypography
                  variant="body2"
                  sx={{
                    margin: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100% - 24px)',
                  }}
                >
                  {formattedLocations}
                </SecondaryTextTypography>
              </Stack>
            )}

            {/* Relatives with icon */}
            {formattedRelatives && formattedRelatives !== '-' && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                <People
                  sx={{ fontSize: '16px', color: theme.palette.text.secondary }}
                />
                <SecondaryTextTypography
                  variant="body2"
                  sx={{
                    margin: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100% - 24px)',
                    textTransform: 'capitalize',
                  }}
                >
                  {formattedRelatives}
                </SecondaryTextTypography>
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
      renderExpandedContent={() => (
        <InfoSection>
          {/* "Is this your relative?" box */}
          <Box
            sx={{
              backgroundColor: theme.palette.grey[100],
              borderRadius: 2,
              p: 2,
              mb: 2,
              mt: 2,
            }}
          >
            <Text variant="body2" color="text.secondary">
              Is this your relative?
            </Text>
            <AddToFamilyTreeButton
              variant="text"
              reportInFamilyTree={reportInTree}
              addRelativeToFamilyTree={onHandleAddToFamilyTree}
              isCreatingFamilyTree={isCreatingFamilyTree}
              showArrowIcon={true}
              sx={{
                p: 0,
                minWidth: 'auto',
                justifyContent: 'flex-start',
                mt: 0.5,
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
                '& .MuiTypography-root': {
                  textDecoration: 'none',
                },
                '&:hover .MuiTypography-root': {
                  textDecoration: 'underline',
                },
              }}
            />
          </Box>

          {aliases && aliases.length > 0 && (
            <>
              <SecondaryTextSubtitleTypography variant="subtitle2">
                Aliases:
              </SecondaryTextSubtitleTypography>
              <CustomList items={aliases} />
            </>
          )}

          {locations &&
            (locations as string[])
              .map(cleanLocationString)
              .filter((loc) => loc).length > 0 && (
              <>
                <SecondaryTextSubtitleTypography variant="subtitle2">
                  Locations:
                </SecondaryTextSubtitleTypography>
                <CustomList
                  items={(locations as string[])
                    .map(cleanLocationString)
                    .filter((loc) => loc)}
                  maxItems={4}
                  preserveCase={true}
                />
              </>
            )}

          {/* Only show Relatives & Associates if not empty */}
          {uniqueRelatives && uniqueRelatives.length > 0 && (
            <>
              <SecondaryTextSubtitleTypography variant="subtitle2">
                Relatives & Associates:
              </SecondaryTextSubtitleTypography>
              <RelativesList
                uniqueRelatives={uniqueRelatives}
                uniqueRelativesIds={uniqueRelativesIds}
                onClickHandler={handleRelativeClick}
                maxItems={5}
              />
            </>
          )}

          {/* View button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleOpenMainPersonReport}
            sx={{
              mt: 3,
              height: '48px',
              borderRadius: '24px',
            }}
          >
            View
          </Button>
        </InfoSection>
      )}
    />
  );
}
